import React, { useState } from 'react';

/* ─── Code always has a dark background in both themes ─────────────── */
const CODE_TEXT = '#e2e4f0';

const Section = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: '3rem', scrollMarginTop: '56px' }}>
    <h2 style={{
      fontSize: '1.4rem',
      fontWeight: 700,
      color: 'var(--accent)',
      borderBottom: '2px solid var(--border)',
      paddingBottom: '0.5rem',
      marginBottom: '1.25rem',
    }}>
      {title}
    </h2>
    {children}
  </section>
);

const SubSection = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h3 style={{
      fontSize: '1.05rem',
      fontWeight: 600,
      color: 'var(--text)',
      marginBottom: '0.6rem',
    }}>
      {title}
    </h3>
    {children}
  </div>
);

const CodeBlock = ({ code, lang = 'javascript' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={{
      position: 'relative',
      background: '#0d0d16',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      marginBottom: '1rem',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.4rem 1rem',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <span style={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: 'JetBrains Mono, monospace' }}>{lang}</span>
        <button
          onClick={handleCopy}
          style={{
            background: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '4px',
            color: copied ? '#43e97b' : '#6b7280',
            fontSize: '0.72rem',
            padding: '2px 8px',
            cursor: 'pointer',
            fontFamily: 'JetBrains Mono, monospace',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre style={{
        margin: 0,
        padding: '1rem',
        overflowX: 'auto',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '0.82rem',
        lineHeight: 1.65,
        color: CODE_TEXT,
        whiteSpace: 'pre',
      }}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const InfoBox = ({ type = 'info', children }) => {
  const colors = {
    info:    { border: 'var(--accent)',  bg: 'rgba(108,99,255,0.07)', icon: 'ℹ️' },
    tip:     { border: 'var(--accent3)', bg: 'rgba(67,233,123,0.07)', icon: '💡' },
    warning: { border: 'var(--put)',     bg: 'rgba(247,183,49,0.07)', icon: '⚠️' },
  };
  const c = colors[type];
  return (
    <div style={{
      border: `1px solid ${c.border}`,
      background: c.bg,
      borderRadius: '8px',
      padding: '0.85rem 1rem',
      marginBottom: '1rem',
      fontSize: '0.88rem',
      lineHeight: 1.6,
      color: 'var(--text)',
      display: 'flex',
      gap: '0.6rem',
    }}>
      <span style={{ flexShrink: 0 }}>{c.icon}</span>
      <div>{children}</div>
    </div>
  );
};

/* inline code chip — adapts to theme */
const IC = ({ children }) => (
  <code style={{
    fontFamily: 'JetBrains Mono, monospace',
    background: 'var(--surface2)',
    color: 'var(--accent)',
    padding: '1px 6px',
    borderRadius: '3px',
    fontSize: '0.87em',
    border: '1px solid var(--border)',
  }}>{children}</code>
);

const FolderTree = () => (
  <CodeBlock lang="folder structure" code={`lobees-api/
├── index.js              ← App entry point: wires all routes, middleware, DB, Socket.io
├── .env                  ← Environment variables (PORT, DB URI, JWT secret, keys)
├── package.json
│
├── routes/               ← One file per feature: defines HTTP endpoints
│   ├── authroute.js
│   ├── leadroute.js
│   ├── contactroute.js
│   └── ...               (90+ route files)
│
├── controllers/          ← One file per feature: handles request/response logic
│   ├── authcontroller.js
│   ├── leadcontroller.js
│   ├── contactcontroller.js
│   └── ...               (100+ controller files)
│
├── models/               ← Mongoose schemas: defines MongoDB collections & fields
│   ├── accountmodel.js
│   ├── leadmodel.js
│   ├── contactmodel.js
│   └── ...               (130+ model files)
│
├── helper/               ← Reusable service functions, organized by domain
│   ├── accounthelper/
│   │   └── accounthelper.js
│   ├── leadhelper/
│   ├── commonhelper/
│   │   └── commonhelper.js   ← Shared utilities (date, OTP, username formatting)
│   ├── saveMailsByParams.js  ← Email sending utility
│   ├── getrandomstring.js
│   └── ...               (90+ helper files/folders)
│
├── middlewares/
│   └── isauth.js         ← JWT authentication guard
│
└── utils/
    ├── dbconnect.js       ← MongoDB connection
    ├── socket.js          ← Socket.io setup for real-time chat
    └── passport.js        ← Passport.js OAuth configuration`} />
);

const tocItems = [
  { id: 'overview',         label: 'Overview' },
  { id: 'folder-structure', label: 'Folder Structure' },
  { id: 'request-flow',     label: 'Request Flow' },
  { id: 'routes',           label: 'Routes' },
  { id: 'controllers',      label: 'Controllers' },
  { id: 'models',           label: 'Models' },
  { id: 'helpers',          label: 'Helpers' },
  { id: 'middlewares',      label: 'Middlewares' },
  { id: 'conventions',      label: 'Conventions' },
  { id: 'adding-feature',   label: 'Adding a Feature' },
];

export default function ApiStructurePage() {
  const [activeId, setActiveId] = useState('overview');

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* ── Fixed top TOC bar ── */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 'var(--sidebar-w)',
        right: 0,
        zIndex: 40,
        background: 'var(--topbar-bg)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)',
        padding: '0 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        overflowX: 'auto',
        height: '48px',
      }}>
        <span style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          marginRight: '0.5rem',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          Jump to:
        </span>
        {tocItems.map((item, i) => (
          <React.Fragment key={item.id}>
            {i > 0 && (
              <span style={{ color: 'var(--border)', fontSize: '0.8rem', flexShrink: 0 }}>·</span>
            )}
            <button
              onClick={() => scrollTo(item.id)}
              style={{
                background: activeId === item.id ? 'rgba(108,99,255,0.15)' : 'none',
                border: activeId === item.id ? '1px solid rgba(108,99,255,0.4)' : '1px solid transparent',
                borderRadius: '6px',
                color: activeId === item.id ? 'var(--accent)' : 'var(--muted)',
                fontSize: '0.78rem',
                fontWeight: activeId === item.id ? 600 : 400,
                padding: '3px 10px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'Syne, sans-serif',
                transition: 'all 0.15s',
                flexShrink: 0,
              }}
            >
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* ── Page content ── */}
      <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', padding: '2rem 1.5rem', paddingTop: 'calc(48px + 2rem)' }}>

        {/* Page Header */}
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '2rem' }}>🏗️</span>
            <h1 style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>
              Backend (API) Project Structure
            </h1>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
            A guide for new team members on how the <strong style={{ color: 'var(--text)' }}>lobees-api</strong> codebase is organized
            and how to contribute new features following established patterns.
          </p>
        </div>

        {/* Overview */}
        <Section id="overview" title="Overview">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            The Lobees API is a <strong style={{ color: 'var(--text)' }}>Node.js + Express</strong> REST API backed by{' '}
            <strong style={{ color: 'var(--text)' }}>MongoDB (Mongoose)</strong>.
            It follows a clean <strong style={{ color: 'var(--text)' }}>Route → Controller → Model</strong> pattern with a
            separate <strong style={{ color: 'var(--text)' }}>Helper</strong> layer for reusable service logic.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
            {[
              { icon: '🗺️', label: 'Routes',      desc: 'Define HTTP endpoints & link to controllers' },
              { icon: '⚙️', label: 'Controllers', desc: 'Handle request/response & orchestrate logic' },
              { icon: '🗃️', label: 'Models',       desc: 'Mongoose schemas for MongoDB collections' },
              { icon: '🔧', label: 'Helpers',      desc: 'Reusable service functions per domain' },
              { icon: '🛡️', label: 'Middlewares',  desc: 'JWT auth guard applied to protected routes' },
            ].map(card => (
              <div key={card.label} style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                padding: '1rem',
              }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{card.icon}</div>
                <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: '0.3rem', fontSize: '0.9rem' }}>{card.label}</div>
                <div style={{ color: 'var(--muted)', fontSize: '0.78rem', lineHeight: 1.5 }}>{card.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {[
              { label: 'Framework',  value: 'Express.js 4.x' },
              { label: 'Database',   value: 'MongoDB + Mongoose' },
              { label: 'Auth',       value: 'JWT + Passport.js' },
              { label: 'Real-time',  value: 'Socket.io' },
              { label: 'Email',      value: 'Nodemailer / SendGrid' },
              { label: 'Background', value: 'Inngest tasks' },
            ].map(item => (
              <div key={item.label} style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: '0.15rem' }}>{item.label}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent)', fontFamily: 'JetBrains Mono, monospace' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Folder Structure */}
        <Section id="folder-structure" title="Folder Structure">
          <FolderTree />
          <InfoBox type="tip">
            Every feature follows the same naming convention: <IC>{'<feature>route.js'}</IC>,{' '}
            <IC>{'<feature>controller.js'}</IC>, <IC>{'<feature>model.js'}</IC>.
            This makes it easy to locate any feature's code instantly.
          </InfoBox>
        </Section>

        {/* Request Flow */}
        <Section id="request-flow" title="Request Flow">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            Trace a request from the client all the way through the stack and back:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: '1.5rem' }}>
            {[
              { step: '1', label: 'HTTP Request',                   detail: 'POST /api/lead/createlead  (Authorization: Bearer <jwt>)',    accent: 'var(--accent)' },
              { step: '2', label: 'index.js',                        detail: 'Express router matches /api/lead → forwards to leadroute.js',  accent: null },
              { step: '3', label: 'routes/leadroute.js',             detail: 'Matches POST /createlead → runs [isauth, createlead]',          accent: null },
              { step: '4', label: 'middlewares/isauth.js',           detail: 'Validates JWT, attaches req.useraccountdata, calls next()',     accent: 'var(--put)' },
              { step: '5', label: 'controllers/leadcontroller.js',   detail: 'Reads req.body & req.useraccountdata, calls model + helpers',  accent: 'var(--accent3)' },
              { step: '6', label: 'models/leadmodel.js',             detail: 'Mongoose saves the document to MongoDB',                       accent: 'var(--patch)' },
              { step: '7', label: 'helper/…',                        detail: 'Optional: action log, email notification, etc.',               accent: null },
              { step: '8', label: 'JSON Response',                   detail: '{ status: true, data: newLead, message: "Lead created" }',     accent: 'var(--accent3)' },
            ].map((item, idx, arr) => (
              <div key={item.step} style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '32px', flexShrink: 0 }}>
                  <div style={{
                    width: '32px', height: '32px',
                    borderRadius: '50%',
                    background: item.accent ? item.accent : 'var(--surface2)',
                    color: item.accent ? '#fff' : 'var(--muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', fontWeight: 700,
                    border: `2px solid ${item.accent || 'var(--border)'}`,
                    flexShrink: 0,
                  }}>
                    {item.step}
                  </div>
                  {idx < arr.length - 1 && (
                    <div style={{ width: '2px', flex: 1, background: 'var(--border)', margin: '2px 0' }} />
                  )}
                </div>
                <div style={{ paddingBottom: idx < arr.length - 1 ? '1rem' : 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text)', fontFamily: 'JetBrains Mono, monospace' }}>{item.label}</div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.15rem' }}>{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Routes */}
        <Section id="routes" title="Routes">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Route files are thin — they only declare HTTP method + path and delegate to controller functions.
            They live in <IC>routes/</IC> and are mounted in <IC>index.js</IC>.
          </p>

          <SubSection title="Pattern">
            <CodeBlock code={`// routes/leadroute.js

const leadrouter = require("express").Router();
const isauth = require("../middlewares/isauth");

const {
  getleads,
  createlead,
  updatelead,
  deletelead,
} = require("../controllers/leadcontroller");

// Public route (no middleware)
leadrouter.get("/getleads", getleads);

// Protected routes (isauth middleware required)
leadrouter.post("/createlead", isauth, createlead);
leadrouter.put("/updatelead/:id", isauth, updatelead);
leadrouter.delete("/deletelead/:id", isauth, deletelead);

module.exports = leadrouter;`} />
          </SubSection>

          <SubSection title="Mounting in index.js">
            <CodeBlock code={`// index.js — mount the router with a base path
const leadroutes = require("./routes/leadroute");

app.use("/api/lead", leadroutes);

// Final URL: GET /api/lead/getleads
//            POST /api/lead/createlead  (Authorization: Bearer <jwt>)`} />
          </SubSection>

          <InfoBox type="info">
            Protected routes require the JWT as an <strong>Authorization header</strong>:{' '}
            <IC>Authorization: Bearer &lt;jwt&gt;</IC> (or an HttpOnly cookie for browser sessions).
            Both are validated by the <IC>isauth</IC> middleware. The old <IC>?token=</IC> query param is no longer supported.
          </InfoBox>
        </Section>

        {/* Controllers */}
        <Section id="controllers" title="Controllers">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Controllers contain the business logic for each route handler. They receive the Express{' '}
            <IC>req</IC> and <IC>res</IC> objects, call models and helpers, then return a JSON response.
          </p>

          <SubSection title="Pattern">
            <CodeBlock code={`// controllers/leadcontroller.js

const Lead = require("../models/leadmodel");
const { saveActionLogs } = require("../helper/actionLogs/saveactionlogs");

// GET all leads
exports.getleads = async (req, res) => {
  try {
    const { ecosystemid } = req.query;

    const leads = await Lead.find({ ecosystemid })
      .populate("owner", "name email")
      .sort({ createdAt: -1 });

    return res.json({ status: true, data: leads, message: "Leads fetched" });
  } catch (error) {
    return res.json({ status: false, data: null, message: error.message });
  }
};

// POST create a lead
exports.createlead = async (req, res) => {
  try {
    const { name, email, phone, ecosystemid } = req.body;

    // req.useraccountdata is set by the isauth middleware
    const createdby = req.useraccountdata.userId;

    const newLead = new Lead({ name, email, phone, ecosystemid, createdby });
    await newLead.save();

    // Log the action
    await saveActionLogs({ action: "CREATE_LEAD", userId: createdby });

    return res.json({ status: true, data: newLead, message: "Lead created" });
  } catch (error) {
    return res.json({ status: false, data: null, message: error.message });
  }
};`} />
          </SubSection>

          <SubSection title="Standard Response Shape">
            <CodeBlock lang="json" code={`{
  "status": true | false,
  "data": { ... } | null,
  "message": "Human-readable result message"
}`} />
            <InfoBox type="tip">
              Always wrap your handler body in a <IC>try/catch</IC> block.
              On error, return <IC>{'{ status: false, data: null, message: error.message }'}</IC>{' '}
              so the client always gets a consistent shape.
            </InfoBox>
          </SubSection>

          <SubSection title="Accessing Auth Data">
            <CodeBlock code={`// After isauth middleware runs, the decoded JWT payload is on:
req.useraccountdata.userId    // The logged-in user's _id
req.useraccountdata.email     // The logged-in user's email`} />
          </SubSection>
        </Section>

        {/* Models */}
        <Section id="models" title="Models">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Models are Mongoose schemas that define the shape of MongoDB documents.
            Each collection has one model file in <IC>models/</IC>.
          </p>

          <SubSection title="Pattern">
            <CodeBlock code={`// models/leadmodel.js

const mongoose = require("mongoose");

const LeadSchema = new mongoose.Schema(
  {
    // Track who created / last updated this document
    createdby: { type: mongoose.Schema.Types.ObjectId, ref: "Account" },
    updatedby: { type: mongoose.Schema.Types.ObjectId, ref: "Account" },

    // Core fields
    name:         { type: String, default: "" },
    email:        { type: String, default: "" },
    phone:        { type: String, default: "" },
    status:       { type: String, default: "new" },

    // Foreign key to another collection
    ecosystemid:  { type: mongoose.Schema.Types.ObjectId, ref: "Ecosystem" },
    owner:        { type: mongoose.Schema.Types.ObjectId, ref: "Account" },

    // Array of references
    tags: [{ type: mongoose.Schema.Types.ObjectId, ref: "Tag" }],
  },
  { timestamps: true }  // Adds createdAt and updatedAt automatically
);

module.exports = mongoose.model("Lead", LeadSchema);`} />
          </SubSection>

          <SubSection title="Key Conventions">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { label: 'ref: "Account"',     desc: 'Cross-collection reference — always use the Model name as the string' },
                { label: 'default: ""',         desc: 'String fields default to empty string, not null' },
                { label: 'timestamps: true',    desc: 'Always include — gives you createdAt / updatedAt for free' },
                { label: 'createdby/updatedby', desc: 'Track ownership on every schema' },
              ].map(item => (
                <div key={item.label} style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '0.75rem',
                }}>
                  <IC>{item.label}</IC>
                  <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0.4rem 0 0', lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </SubSection>
        </Section>

        {/* Helpers */}
        <Section id="helpers" title="Helpers">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Helpers are <strong style={{ color: 'var(--text)' }}>service functions</strong> or{' '}
            <strong style={{ color: 'var(--text)' }}>shared utilities</strong> extracted
            out of controllers to keep them lean. If the same logic would be used in more than one controller,
            or if a piece of logic is complex enough to deserve its own file, it belongs in <IC>helper/</IC>.
          </p>

          <SubSection title="Organisation">
            <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '0.75rem', fontSize: '0.88rem' }}>
              Domain-specific helpers live in a subfolder. Standalone utility functions live as individual files.
            </p>
            <CodeBlock code={`helper/
├── accounthelper/
│   └── accounthelper.js   ← connecttoadmin(), savelogintimestamp(), newAccountToSave()
├── leadhelper/
│   └── leadhelper.js
├── commonhelper/
│   └── commonhelper.js    ← getNextDate(), formattedUsername(), createNewOtp()
├── actionLogs/
│   └── saveactionlogs.js  ← saveActionLogs() — call after any create/update/delete
├── saveMailsByParams.js   ← Send emails via Nodemailer/SendGrid
├── getrandomstring.js     ← Generate a random token/code
└── getcurrentdate.js      ← Get today's date as a formatted string`} />
          </SubSection>

          <SubSection title="Example Helper Function">
            <CodeBlock code={`// helper/accounthelper/accounthelper.js

const Accounts = require("../../models/accountmodel");
const Contact  = require("../../models/contactmodel");

// Called after registration to auto-connect the new user to the admin account
exports.connecttoadmin = async (userId, currentEcosystemId) => {
  const admin = await Accounts.findOne({ role: { slug: "SUPER_ADMIN" } });
  if (!admin) return;

  const alreadyConnected = await Contact.findOne({
    $or: [
      { owner: userId, member: admin._id },
      { owner: admin._id, member: userId },
    ],
  });

  if (!alreadyConnected) {
    await new Contact({ owner: admin._id, member: userId, ecosystemid: currentEcosystemId }).save();
  }
};`} />
          </SubSection>

          <SubSection title="Using a Helper in a Controller">
            <CodeBlock code={`// controllers/authcontroller.js

const { connecttoadmin }  = require("../helper/accounthelper/accounthelper");
const { saveActionLogs }  = require("../helper/actionLogs/saveactionlogs");
const saveMailByParams    = require("../helper/saveMailsByParams");

exports.registerprocess = async (req, res) => {
  // ...create account...
  await connecttoadmin(newAccount._id, ecosystemId);
  await saveActionLogs({ action: "REGISTER", userId: newAccount._id });
  await saveMailByParams({ to: email, subject: "Welcome!", template: "welcome" });
  return res.json({ status: true, data: newAccount, message: "Registered" });
};`} />
          </SubSection>

          <InfoBox type="tip">
            When you find yourself writing the same block of code in two controllers, extract it into a helper.
            Name helper files after the domain they serve: <IC>leadhelper.js</IC>,{' '}
            <IC>commonhelper.js</IC>, etc.
          </InfoBox>
        </Section>

        {/* Middlewares */}
        <Section id="middlewares" title="Middlewares">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Currently the project has one primary middleware:{' '}
            <strong style={{ color: 'var(--text)' }}>isauth</strong> — the JWT authentication guard.
          </p>

          <SubSection title="isauth.js — JWT Guard">
            <CodeBlock code={`// middlewares/isauth.js

const jwt = require("jsonwebtoken");

module.exports = async (req, res, next) => {
  // Accepts token from HttpOnly cookie OR Authorization: Bearer header
  const cookieToken = req.cookies.token;
  const authHeader = req.headers["authorization"];
  const bearerToken = authHeader && authHeader.startsWith("Bearer ")
    ? authHeader.slice(7)
    : null;
  const token = cookieToken || bearerToken;

  if (!token) {
    return res.json({ status: false, message: "Not authenticated." });
  }

  try {
    const decodedToken = jwt.verify(token, process.env.JWT_SECRET || "secret");
    // Attach decoded payload so controllers can read the user's info
    req.useraccountdata = decodedToken;
    next();
  } catch (error) {
    return res.json({ status: false, message: "Token Missing or Expired" });
  }
};`} />
          </SubSection>

          <SubSection title="How to Use It">
            <CodeBlock code={`// In a route file — add isauth between path and handler to protect the route
const isauth = require("../middlewares/isauth");

router.post("/create", isauth, myController);   // Protected
router.get("/list",    myController);            // Public (no guard)`} />
          </SubSection>

          <InfoBox type="warning">
            Never skip <IC>isauth</IC> on routes that create, update, or delete data.
            Read-only public endpoints may omit it, but double-check with the team before making a route public.
          </InfoBox>
        </Section>

        {/* Conventions */}
        <Section id="conventions" title="Conventions & Rules">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {[
              {
                title: 'Naming',
                items: [
                  'Files are all lowercase with no spaces',
                  'Route file: featureroute.js',
                  'Controller: featurecontroller.js',
                  'Model: featuremodel.js',
                  'Helper dir: featurehelper/featurehelper.js',
                ],
              },
              {
                title: 'Responses',
                items: [
                  'Always return JSON with status / data / message',
                  'status: true on success, false on error',
                  'data: null when nothing to return',
                  'Never throw uncaught errors — use try/catch',
                  'Use async/await, not callbacks',
                ],
              },
              {
                title: 'Authentication',
                items: [
                  'Pass JWT as Authorization: Bearer <token> header (or cookie)',
                  'Use isauth middleware on all write routes',
                  'Access user via req.useraccountdata.userId',
                  'Always store createdby on new documents',
                ],
              },
              {
                title: 'Code Organisation',
                items: [
                  'Keep controllers focused on request/response',
                  'Extract reusable logic into helpers',
                  'One model per MongoDB collection',
                  'Register every new route in index.js',
                  'Log significant actions with saveActionLogs()',
                ],
              },
            ].map(block => (
              <div key={block.title} style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                padding: '1rem 1.25rem',
              }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--accent)', marginBottom: '0.6rem' }}>{block.title}</div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'var(--muted)', fontSize: '0.82rem', lineHeight: 1.8 }}>
                  {block.items.map(item => <li key={item}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Adding a Feature */}
        <Section id="adding-feature" title="How to Add a New Feature">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            Follow these steps every time you add a new resource (e.g. <em>Task</em>):
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {[
              {
                num: '01',
                title: 'Create the Model',
                desc: 'Define the MongoDB schema in models/',
                code: `// models/taskmodel.js
const mongoose = require("mongoose");

const TaskSchema = new mongoose.Schema({
  createdby: { type: mongoose.Schema.Types.ObjectId, ref: "Account" },
  updatedby: { type: mongoose.Schema.Types.ObjectId, ref: "Account" },
  title:       { type: String, default: "" },
  description: { type: String, default: "" },
  status:      { type: String, default: "pending" },
  assignedto:  { type: mongoose.Schema.Types.ObjectId, ref: "Account" },
  ecosystemid: { type: mongoose.Schema.Types.ObjectId, ref: "Ecosystem" },
}, { timestamps: true });

module.exports = mongoose.model("Task", TaskSchema);`,
              },
              {
                num: '02',
                title: 'Create the Controller',
                desc: 'Write request handlers in controllers/',
                code: `// controllers/taskcontroller.js
const Task = require("../models/taskmodel");
const { saveActionLogs } = require("../helper/actionLogs/saveactionlogs");

exports.gettasks = async (req, res) => {
  try {
    const tasks = await Task.find({ ecosystemid: req.query.ecosystemid });
    return res.json({ status: true, data: tasks, message: "Tasks fetched" });
  } catch (error) {
    return res.json({ status: false, data: null, message: error.message });
  }
};

exports.createtask = async (req, res) => {
  try {
    const { title, description, ecosystemid } = req.body;
    const createdby = req.useraccountdata.userId;
    const task = await new Task({ title, description, ecosystemid, createdby }).save();
    await saveActionLogs({ action: "CREATE_TASK", userId: createdby });
    return res.json({ status: true, data: task, message: "Task created" });
  } catch (error) {
    return res.json({ status: false, data: null, message: error.message });
  }
};`,
              },
              {
                num: '03',
                title: 'Create the Route',
                desc: 'Wire up HTTP methods in routes/',
                code: `// routes/taskroute.js
const taskrouter = require("express").Router();
const isauth = require("../middlewares/isauth");
const { gettasks, createtask } = require("../controllers/taskcontroller");

taskrouter.get("/gettasks",    gettasks);
taskrouter.post("/createtask", isauth, createtask);

module.exports = taskrouter;`,
              },
              {
                num: '04',
                title: 'Register in index.js',
                desc: 'Mount the router on a base path',
                code: `// index.js
const taskroutes = require("./routes/taskroute");

app.use("/api/task", taskroutes);

// Done! Your endpoints are now live:
// GET  /api/task/gettasks
// POST /api/task/createtask  (Authorization: Bearer <jwt>)`,
              },
              {
                num: '05',
                title: '(Optional) Add Helpers',
                desc: 'Extract complex logic into helper/taskhelper/',
                code: `// helper/taskhelper/taskhelper.js

const Task = require("../../models/taskmodel");

// Example: notify assignee when a task is created
exports.notifyAssignee = async (taskId, assignedToId) => {
  // ... look up assignee, send email/notification ...
};`,
              },
            ].map(step => (
              <div key={step.num} style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem 1.25rem',
                  background: 'var(--surface2)',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    color: 'var(--accent)',
                  }}>{step.num}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text)' }}>{step.title}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{step.desc}</div>
                  </div>
                </div>
                <div style={{ padding: '1rem' }}>
                  <CodeBlock code={step.code} />
                </div>
              </div>
            ))}
          </div>

          <InfoBox type="tip">
            That's the complete cycle. Every feature in the codebase was added following exactly these steps.
            When in doubt, look at an existing similar feature (e.g. <IC>leadroute.js</IC> /{' '}
            <IC>leadcontroller.js</IC>) and follow the same pattern.
          </InfoBox>
        </Section>

      </div>
    </div>
  );
}
